import { ArrowRight } from "lucide-react";
import { IMAGES } from "@/lib/site";

export default function Hero({ onBookDemo }) {
  return (
    <section
      id="top"
      className="relative pt-[110px] lg:pt-[124px] pb-10 lg:pb-12 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 bg-grid opacity-[0.18]" aria-hidden />
      <div
        className="absolute -top-32 right-0 w-[820px] h-[820px] pointer-events-none"
        style={{ background: "radial-gradient(closest-side, rgba(96,165,250,0.16), transparent)" }}
        aria-hidden
      />

      <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 lg:gap-12 items-center min-h-[560px] lg:min-h-[640px]">
          {/* LEFT — content */}
          <div className="relative z-10">
            <div className="eyebrow flex items-center gap-3" data-testid="hero-eyebrow">
              <span className="inline-block w-6 h-px bg-sapphire-glow/60" />
              ESTHERA REALITYOS — THE AI OPERATING SYSTEM FOR REAL ESTATE
            </div>

            <h1
              className="font-display mt-7 text-platinum text-balance text-[34px] sm:text-[48px] lg:text-[56px] xl:text-[60px] leading-[1.05] sm:leading-[1.04] tracking-[-0.018em]"
              data-testid="hero-headline"
            >
              <span className="block whitespace-normal sm:whitespace-nowrap">The AI Operating System</span>
              <span
                className="text-sapphire-glow block"
                style={{ textShadow: "0 0 40px rgba(96,165,250,0.28)" }}
              >
                for Real Estate
              </span>
            </h1>

            <div
              className="font-display mt-6 text-platinum/85 text-[20px] lg:text-[22px] leading-[1.3] italic tracking-[-0.005em]"
              data-testid="hero-subheadline"
            >
              Where Real Estate Meets Intelligence
            </div>

            <p
              className="mt-5 text-[15px] lg:text-[16px] leading-[1.7] text-platinum/65 max-w-[520px]"
              data-testid="hero-subcopy"
            >
              Unify data. Automate operations. Empower teams. Drive measurable
              growth across every asset, workflow, and market.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                data-testid="hero-book-demo"
                onClick={onBookDemo}
                className="btn-primary rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2"
              >
                Book a Demo
                <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="/platform"
                data-testid="hero-explore-platform"
                className="btn-ghost rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2"
              >
                Explore Platform
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            {/* Esthera slogan strip — directly beneath CTAs */}
            <div className="mt-10 flex items-center gap-4" data-testid="hero-slogan">
              <span className="inline-block w-10 h-px bg-sapphire-glow/55" />
              <span className="text-[11.5px] tracking-[0.32em] text-sapphire-glow uppercase font-medium">
                One Platform. Infinite Intelligence.
              </span>
              <span className="inline-block flex-1 h-px bg-gradient-to-r from-sapphire-glow/40 to-transparent" />
            </div>
          </div>

          {/* RIGHT — uploaded skyline image (Image 1) — bleeds beyond container, fades into page */}
          <div className="relative lg:-mr-10 xl:-mr-16" data-testid="hero-visual">
            {/* Atmospheric blue depth behind the image center */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(60% 50% at 55% 50%, rgba(37,99,235,0.18), transparent 70%)",
                filter: "blur(40px)",
              }}
              aria-hidden
            />
            <div className="relative">
              <img
                src={IMAGES.heroSkyline}
                alt="Esthera Intelligence Network — connected real estate ecosystem"
                className="block w-full h-[500px] lg:h-[620px] object-cover object-center"
                style={{
                  WebkitMaskImage:
                    "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                  maskImage:
                    "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                }}
                draggable="false"
              />
              {/* Edge fades that blend the image into the obsidian background */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(10,15,26,0.55) 0%, rgba(10,15,26,0) 18%, rgba(10,15,26,0) 70%, rgba(10,15,26,0.95) 100%)",
                }}
                aria-hidden
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(10,15,26,0.85) 0%, rgba(10,15,26,0.20) 16%, rgba(10,15,26,0) 35%, rgba(10,15,26,0) 78%, rgba(10,15,26,0.55) 100%)",
                }}
                aria-hidden
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
